<script lang="ts">
  import { untrack } from "svelte";
  import { goto } from "$app/navigation";
  import { page } from "$app/state";
  import { toast } from "svelte-sonner";
  import { reportUiFailure } from "$lib/report-failure.js";
  import CircleAlertIcon from "@lucide/svelte/icons/circle-alert";
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw";
  import HistoryIcon from "@lucide/svelte/icons/history";
  import {
    workspaceState,
    channelState,
    client,
    listChannels,
    joinChannel,
    selectChannelPreview,
    searchTasks,
    searchPages,
    authState,
    cyboState,
  } from "$lib/state/app.svelte.js";
  import { memberEmailLocalPart } from "$lib/member-email";
  import type { Channel, FormerParticipant, Message } from "$lib/core/types.js";
  import { cn, formatMessageTimestamp } from "$lib/utils.js";
  import { liveHumanName } from "$lib/components/message/message-author.js";
  import { relayedRowOf } from "$lib/components/message/relayed-row.js";
  import { RELAYED_CHIP } from "$lib/components/message/relayed-source.js";
  import {
    MIN_QUERY_LENGTH,
    highlightParts,
    matchChannels,
    matchPeople,
    matchScore,
    type BrowseChannel,
    type ChannelHit,
    type PersonCandidate,
    type PersonHit,
    type SearchSection,
  } from "$lib/search/global-search.js";
  import { GlobalSearchState, type SearchLegs } from "$lib/search/global-search-state.svelte.js";
  import { registerTopSearch } from "$lib/search/header-search.js";
  import { messageResultView, messagePeerName } from "$lib/search/message-result-view.js";
  import {
    ALL_WORKSPACES_LIMIT,
    previewPerWorkspace,
    searchAllWorkspaces,
    type AllWorkspacesResult,
  } from "$lib/search/all-workspaces-search.js";
  import { classifySearchResult } from "$lib/message-search-result.js";
  import {
    scopedSearch,
    SECTION_FILTER_LABEL,
    RECENTS_SHOWN,
    type RecentSearch,
    type SearchScope,
    type SearchScopeKind,
    type SearchSectionFilter,
  } from "$lib/search/scoped-search.svelte.js";
  import type { MessageSearchScope } from "$lib/core/client.js";
  import { searchPeopleCandidates } from "$lib/search/people-candidates.js";
  import { activeFilters, type ActiveFilterKey } from "$lib/search/active-filters.js";
  import { isMe } from "$lib/session-scope.js";
  import { nameKey, parseSearchQuery, removeTokens, resolveName, type PositionedToken } from "$lib/search/search-query.js";
  import ActivityActorAvatar from "$lib/components/panes/ActivityActorAvatar.svelte";
  import { Button } from "$lib/components/ui/button/index.js";
  import ChannelGlyph from "./ChannelGlyph.svelte";
  import ExternalBotMark from "$lib/components/agents/external/ExternalBotMark.svelte";
  import { externalAuthor } from "$lib/external-agent-setup.js";
  import TaskSearchResults from "$lib/components/tasks/TaskSearchResults.svelte";
  import PageSearchResults from "$lib/components/tasks/PageSearchResults.svelte";
  import SkillSearchResults from "$lib/components/panes/SkillSearchResults.svelte";
  import type { SkillSearchHit } from "$lib/components/panes/skills-library-view-model.js";
  import { onSkillsPlace, openSkillHit, openSkillsTarget, quickSkillsLeg, searchSkillsLeg } from "$lib/search/skills-search-leg.js";

  // The browse list (public channels you are not in) is fetched once per window, not
  // per keystroke.
  const BROWSE_TTL_MS = 30_000;

  let query = $state("");
  let open = $state(false);
  // The `in:<place>` scope the bar is pinned to (set by the header magnifier / ⌘F), or null
  // for whole-workspace search. Its filter toggles live alongside it and reset when it clears.
  let scope = $state<SearchScope | null>(null);
  // `in:Docs` (the Docs sidebar magnifier): only that section is searched. Exclusive with `scope`.
  let sectionFilter = $state<SearchSectionFilter | null>(null);
  let fromMe = $state(false);
  let mentionsMe = $state(false);
  let hasLink = $state(false);
  // `has:file` — a scope flag like hasLink; sent as the relay's `hasFile`, ignored if unsupported.
  let hasFile = $state(false);
  // `from:@person` chips: who the results must come from (besides From me).
  let fromPeople = $state<{ id: string; label: string }[]>([]);
  // `mentions:@person` chips: who the results must mention (the id is sent as `mentionIds`).
  let mentionPeople = $state<{ id: string; label: string }[]>([]);
  // Typed filters that matched nothing (`in:@nobody`) or that search can't honour yet: shown, never dropped.
  let filterNotice = $state<string | null>(null);
  // "All workspaces": the same message search in every workspace the person is in (unscoped
  // only). `across` is what the last such search found besides the rows themselves.
  let allWorkspaces = $state(false);
  let across = $state<Omit<AllWorkspacesResult, "messages"> | null>(null);
  // Another workspace's channels and people, asked once per session of this panel.
  const channelLists = new Map<string, Promise<Channel[]>>();
  const peopleLists = new Map<string, Promise<Array<{ id: string; name: string }>>>();
  const agentLists = new Map<string, Promise<Array<{ id: string; name: string }>>>();
  let acrossRun = 0;
  const canSearchAll = $derived(workspaceState.list.length > 1);
  const searchingAll = $derived(allWorkspaces && canSearchAll && !scope && !sectionFilter);
  // A workspace that could not be searched: its absence is never "No results".
  const acrossFailed = $derived(searchingAll && (across?.failed.length ?? 0) > 0);
  // The three legs and their sections (shared with the mobile overlay). Each leg fails only its
  // own section ("Couldn't search …"), never as "No results" and never the other sections.
  const search = new GlobalSearchState();
  let browse = $state<{ wsId: string; rows: BrowseChannel[]; at: number } | null>(null);
  let joiningId = $state<string | null>(null);
  let activeIndex = $state(0);
  let containerEl = $state<HTMLDivElement | null>(null);
  let inputEl = $state<HTMLInputElement | null>(null);
  let listEl = $state<HTMLDivElement | null>(null);

  // Every workspace gets its first rows before "Show N more", not only the first group.
  const visibleMessages = $derived(
    searchingAll && !search.messagesExpanded ? previewPerWorkspace(search.results) : search.visibleMessages,
  );
  const wsId = $derived(workspaceState.current?.id ?? null);
  const trimmed = $derived(query.trim());
  // A place or any filter narrows to messages: channel / people / task / doc rows can't honour them.
  const messagesOnly = $derived(
    scope !== null ||
      fromMe ||
      mentionsMe ||
      hasLink ||
      hasFile ||
      fromPeople.length > 0 ||
      mentionPeople.length > 0,
  );
  // #689: full channel objects (not just names) so a result can render the right
  // glyph — public (hash) vs private (lock) vs group DM (people) — instead of a
  // hash for every hit.
  const channelsById = $derived(new Map(workspaceState.channels.map((c) => [c.id, c])));

  const livePeople = $derived(searchPeopleCandidates());
  // Removed people and deleted agents (relay-flagged on list_members), read once per window.
  let former = $state<{ wsId: string; rows: FormerParticipant[]; at: number } | null>(null);
  const formerById = $derived(
    new Map((former && former.wsId === wsId ? former.rows : []).map((f) => [f.id, f])),
  );
  // Offered after the live people, never shadowing one (a re-invited person is live again).
  const peopleCandidates = $derived<PersonCandidate[]>([
    ...livePeople,
    ...[...formerById.values()]
      .filter((f) => !livePeople.some((p) => p.id === f.id))
      .map((f) => ({
        id: f.id,
        kind: f.kind === "agent" ? ("agent" as const) : ("dm" as const),
        name: f.name,
        email: null,
        gone: f.kind === "agent" ? ("deleted" as const) : ("deactivated" as const),
      })),
  ]);

  // The current user — excluded from peopleCandidates, but a valid from:/mentions: target (me-filter).
  const selfMember = $derived(workspaceState.members.find((m) => isMe(m.userId, authState.user)));
  const selfId = $derived(selfMember?.userId ?? "");
  const selfName = $derived(
    selfMember ? (selfMember.name ?? authState.getMemberName(selfMember.userId) ?? "") : "",
  );

  const browseRows = $derived(browse && browse.wsId === wsId ? browse.rows : null);
  // A hit can be in a public channel the person has not joined: named from the browse list.
  const browseById = $derived(new Map((browseRows ?? []).map((c) => [c.id, c])));
  // Scoped search is messages-only: a channel/people jump-to row is a whole-workspace
  // navigation, so it is suppressed while the bar is pinned to one place.
  // …and while searching every workspace, where a jump-to row would only be this workspace's.
  const jumpRows = $derived(open && !messagesOnly && !searchingAll && !sectionFilter);
  const channelHits = $derived(jumpRows ? matchChannels(workspaceState.channels, browseRows, trimmed) : []);
  const personHits = $derived(jumpRows ? matchPeople(livePeople, trimmed) : []);

  // The relay's workspace floor answer (see isWorkspaceRefusal): declared before `options`, which reads it.
  let accessRefused = $state<{ wsId: string; at: number } | null>(null);
  const searchRefused = $derived(accessRefused !== null && accessRefused.wsId === wsId);

  // An operator/value typeahead row (from:/in:/mentions:/has:), keyboard-selectable.
  type Suggestion =
    | { kind: "me"; label: string }
    | { kind: "person"; id: string; name: string; email: string | null; agent: boolean; gone?: "deactivated" | "deleted" }
    | { kind: "channel"; id: string; name: string; isPrivate: boolean }
    // `in:Docs` / `in:Tasks`: a whole result section, applied through applySection.
    | { kind: "section"; section: SearchSectionFilter; label: string }
    | { kind: "has"; value: "link" | "file"; label: string };

  type SearchOption =
    | { kind: "channel"; hit: ChannelHit }
    | { kind: "person"; hit: PersonHit }
    | { kind: "message"; message: Message }
    // Task and page rows are links owned by their result components: Enter clicks the row.
    | { kind: "task" }
    | { kind: "page" }
    | { kind: "skill"; hit: SkillSearchHit }
    // The scoped empty-state rows (before anything is typed): the "Show results for"
    // affordance, then recent searches — both keyboard-selectable (design frame Búsqueda 1).
    | { kind: "show-results" }
    | { kind: "recent"; recent: RecentSearch }
    | { kind: "suggest"; suggest: Suggestion };

  // Scoped + nothing typed yet: the dropdown shows the affordance + recents instead of results.
  const scopedEmptyState = $derived((!!scope || !!sectionFilter) && trimmed.length < MIN_QUERY_LENGTH);
  // The unscoped bar with nothing typed: recents + the 2×2 filter grid (the empty card).
  const unscopedEmptyCard = $derived(!scope && !sectionFilter && trimmed.length < MIN_QUERY_LENGTH);
  const shownRecents = $derived(scopedSearch.recents.slice(0, RECENTS_SHOWN));
  const filteredPrompt = $derived.by(() => {
    if (sectionFilter) return `Search ${SECTION_FILTER_LABEL[sectionFilter].toLowerCase()}`;
    return scope ? `Search in ${scope.label}` : null;
  });
  const filterLabel = $derived(scope?.label ?? (sectionFilter ? SECTION_FILTER_LABEL[sectionFilter] : null));
  const scopePrivate = $derived(scope?.kind === "channel" && channelsById.get(scope.id)?.isPrivate === true);
  // Every active filter, in bar order: the chips in the bar and Backspace both read this one list.
  const barFilters = $derived(
    activeFilters({
      scope,
      scopePrivate,
      sectionFilter,
      fromPeople,
      fromMe,
      mentionPeople,
      mentionsMe: mentionsMe && (!scope || scope.kind === "channel"),
      hasLink,
      hasFile,
      allWorkspaces: searchingAll,
    }),
  );

  // An operator typed at the end (`from:ca`, `in:#gen`): drives the avatar typeahead; editing
  // mid-text falls back to results.
  const OP_AT_END = /(?:^|\s)(from|in|mentions|has):([^\s]*)$/i;
  const typeahead = $derived.by(() => {
    if (!open) return null;
    const m = OP_AT_END.exec(query);
    if (!m) return null;
    const sep = m[0].length - (m[1].length + 1 + m[2].length);
    return { op: m[1].toLowerCase(), partial: m[2], start: query.length - m[0].length + sep };
  });

  interface ScoredSuggestion {
    s: Suggestion;
    score: number;
    agent: boolean;
    name: string;
  }

  // `in:` channel rows (unarchived) plus the Docs/Tasks section rows (unless a `#` sigil was typed).
  function inSuggestions(term: string, sigilChannel: boolean): ScoredSuggestion[] {
    const out: ScoredSuggestion[] = [];
    for (const c of workspaceState.channels) {
      if (c.isArchived) continue;
      const score = term ? matchScore(c.name, term) : 0;
      if (score < 0) continue;
      out.push({ s: { kind: "channel", id: c.id, name: c.name, isPrivate: c.isPrivate }, score, agent: false, name: c.name });
    }
    if (sigilChannel) return out;
    for (const section of ["pages", "tasks", "skills"] as SearchSectionFilter[]) {
      const label = SECTION_FILTER_LABEL[section];
      const score = term ? matchScore(label, term) : 0;
      if (score < 0) continue;
      out.push({ s: { kind: "section", section, label }, score, agent: false, name: label });
    }
    return out;
  }

  // People + agents matching by name or handle/alias only — never email (CHANGE 3).
  function personSuggestions(term: string): ScoredSuggestion[] {
    const out: ScoredSuggestion[] = [];
    for (const p of peopleCandidates) {
      const score = term ? Math.max(matchScore(p.name, term), matchScore(p.alsoMatches, term)) : 0;
      if (score < 0) continue;
      const agent = p.kind === "agent";
      out.push({ s: { kind: "person", id: p.id, name: p.name, email: p.email, agent, gone: p.gone }, score, agent, name: p.name });
    }
    return out;
  }

  // from:/mentions: extras — the "me" row + the current user shown by name; picking either applies the me-filter.
  function personOpExtras(op: string, term: string, lowerTerm: string): ScoredSuggestion[] {
    const out: ScoredSuggestion[] = [];
    const selfMatches = selfName !== "" && matchScore(selfName, term) >= 0;
    if (term === "" || "me".startsWith(lowerTerm) || selfMatches) {
      out.push({
        s: { kind: "me", label: op === "from" ? "From me" : "Mentions me" },
        score: term ? 3 : 2,
        agent: false,
        name: "me",
      });
    }
    if (selfName !== "" && (term === "" || selfMatches)) {
      out.push({
        s: { kind: "person", id: selfId, name: selfName, email: null, agent: false },
        score: term ? matchScore(selfName, term) : 0,
        agent: false,
        name: selfName,
      });
    }
    return out;
  }

  // Bare `in:`: a short cap per group so channels never crowd out DMs, agents or the sections.
  function groupCappedIn(ranked: ScoredSuggestion[]): Suggestion[] {
    const channels = ranked.filter((x) => x.s.kind === "channel").slice(0, 5);
    const dms = ranked.filter((x) => x.s.kind === "person" && !x.agent).slice(0, 4);
    const agents = ranked.filter((x) => x.s.kind === "person" && x.agent).slice(0, 4);
    const sections = ranked.filter((x) => x.s.kind === "section");
    return [...channels, ...dms, ...agents, ...sections].map((x) => x.s);
  }

  const suggestions = $derived.by<Suggestion[]>(() => {
    const t = typeahead;
    if (!t) return [];
    if (t.op === "has") {
      const value = t.partial.toLowerCase().replace(/^@/, "");
      return (
        [
          { kind: "has", value: "link", label: "Has a link" },
          { kind: "has", value: "file", label: "Has a file" },
        ] as Suggestion[]
      ).filter((o) => o.kind === "has" && o.value.startsWith(value));
    }
    const sigilChannel = t.partial.startsWith("#");
    const sigilPerson = t.partial.startsWith("@");
    const term = t.partial.replace(/^[#@]/, "");
    const lowerTerm = term.toLowerCase();
    const scored: ScoredSuggestion[] = [];
    if ((t.op === "from" || t.op === "mentions") && !sigilChannel)
      scored.push(...personOpExtras(t.op, term, lowerTerm));
    if (t.op === "in" && !sigilPerson) scored.push(...inSuggestions(term, sigilChannel));
    if (!sigilChannel) scored.push(...personSuggestions(term));
    // People before agents (mockup order) outranks score — a prefix agent never beats a substring person.
    const namePrefix = (name: string): boolean => lowerTerm !== "" && name.toLowerCase().startsWith(lowerTerm);
    const ranked = scored.sort(
      (a, b) =>
        Number(a.agent) - Number(b.agent) ||
        b.score - a.score ||
        Number(namePrefix(b.name)) - Number(namePrefix(a.name)) ||
        a.name.localeCompare(b.name),
    );
    if (t.op === "in" && !sigilPerson && term === "") return groupCappedIn(ranked);
    return ranked.slice(0, 8).map((x) => x.s);
  });
  const typeaheadActive = $derived(suggestions.length > 0);

  // Rows each section puts in the keyboard index space. Only VISIBLE rows count (a "Show N more"
  // tail is a plain button outside it), and none while loading, refused, or the section failed.
  const sectionRows = $derived.by<Record<SearchSection, number>>(() => {
    const hidden = search.loading || searchRefused || !wsId;
    return {
      messages: search.loading || search.messagesFailed ? 0 : visibleMessages.length,
      tasks: hidden || search.tasksFailed ? 0 : search.visibleTasks.length,
      pages: hidden || search.pagesFailed ? 0 : search.visiblePages.length,
      skills: hidden || search.skillsFailed ? 0 : search.visibleSkills.length,
    };
  });
  // Keyboard order = render order: channels, people, then the sections in sectionOrder.
  const sectionStart = $derived.by<Record<SearchSection, number>>(() => {
    const start = { messages: 0, tasks: 0, pages: 0, skills: 0 };
    let at = channelHits.length + personHits.length;
    for (const section of search.sectionOrder) {
      start[section] = at;
      at += sectionRows[section];
    }
    return start;
  });
  const options = $derived.by<SearchOption[]>(() => {
    if (typeaheadActive) return suggestions.map((suggest) => ({ kind: "suggest" as const, suggest }));
    if (unscopedEmptyCard) return shownRecents.map((recent) => ({ kind: "recent" as const, recent }));
    if (scopedEmptyState)
      return [
        { kind: "show-results" as const },
        ...shownRecents.map((recent) => ({ kind: "recent" as const, recent })),
      ];
    return [
      ...channelHits.map((hit) => ({ kind: "channel" as const, hit })),
      ...personHits.map((hit) => ({ kind: "person" as const, hit })),
      ...search.sectionOrder.flatMap(sectionOptions),
    ];
  });
  const isEmpty = $derived(channelHits.length === 0 && personHits.length === 0 && search.foundNothing);

  // One section's keyboard rows, in render order.
  function sectionOptions(section: SearchSection): SearchOption[] {
    if (section === "messages") return visibleMessages.slice(0, sectionRows.messages).map((message) => ({ kind: "message" as const, message }));
    if (section === "skills") return search.visibleSkills.slice(0, sectionRows.skills).map((hit) => ({ kind: "skill" as const, hit }));
    return Array.from({ length: sectionRows[section] }, () => ({ kind: section === "tasks" ? ("task" as const) : ("page" as const) }));
  }

  function viewFor(m: Message) {
    const ws = m.workspaceId;
    if (!ws || ws === wsId) {
      return messageResultView(m, wsId, (id) => channelsById.get(id) ?? browseById.get(id));
    }
    // A hit of another workspace: its channels and people come from that workspace's own lists.
    // A DM peer who is one of its agents, or none of its people, is an agent (classifyDmHit).
    const people = across?.people.get(ws);
    const agents = across?.agents.get(ws);
    return classifySearchResult(m, {
      workspaceId: ws,
      viewer: authState.user,
      getChannel: (id) => across?.channels.get(ws)?.get(id),
      getPeerName: (id) => people?.get(id) ?? agents?.get(id) ?? (m.fromId === id ? m.fromName : undefined),
      isAgentPeer: (id) => agents?.has(id) ?? false,
      isHumanMember: people ? (id) => people.has(id) : undefined,
    });
  }

  // "(deactivated)" / "(deleted)": only what the relay flagged on this hit, never a guess.
  const authorGone = (m: Message): "deactivated" | "deleted" | null => m.fromGone ?? null;
  function peerGone(m: Message, peerId: string | null | undefined): "deactivated" | "deleted" | null {
    if (!peerId) return null;
    if (peerId === m.toId) return m.toGone ?? null;
    return peerId === m.fromId ? (m.fromGone ?? null) : null;
  }

  // Removed people change rarely: read once per 10 min; a failed read is retried after 60 s.
  const FORMER_TTL_MS = 10 * 60_000;
  const FORMER_RETRY_MS = 60_000;
  let formerAsking: string | null = null;
  const formerFresh = (ws: string): boolean =>
    former !== null && former.wsId === ws && Date.now() - former.at <= FORMER_TTL_MS;
  async function loadFormer(ws: string): Promise<void> {
    if (formerAsking === ws) return;
    formerAsking = ws;
    try {
      const res = await client.listMembers(ws, { withFormer: true });
      // An older relay answers without `former`: nobody is offered as removed.
      former = { wsId: ws, rows: res.former ?? [], at: Date.now() };
      // A typed filter naming a removed person was waiting on this list: resolve it now.
      if (filterNotice !== null && wsId === ws && absorbTypedFilters()) void run();
    } catch (err) {
      reportUiFailure("message_search.former", err, { workspaceId: ws });
      const rows = former?.wsId === ws ? former.rows : [];
      former = { wsId: ws, rows, at: Date.now() - FORMER_TTL_MS + FORMER_RETRY_MS };
    } finally {
      if (formerAsking === ws) formerAsking = null;
    }
  }


  const workspaceName = (id: string | undefined): string =>
    workspaceState.list.find((w) => w.id === id)?.name ?? "Another workspace";

  // The relay's workspace floor refuses search to someone no longer in the workspace. That
  // is a fixed answer, not a failure: remembered per window and shown without Try again.

  function isWorkspaceRefusal(err: unknown): boolean {
    if (!(err instanceof Error)) return false;
    const code = (err as Error & { code?: string }).code;
    return code === "not_a_member" || err.message === "not a member of this workspace";
  }

  // True while a remembered answer for this workspace is younger than the window.
  function withinWindow(entry: { wsId: string; at: number } | null, ws: string): boolean {
    return entry !== null && entry.wsId === ws && Date.now() - entry.at <= BROWSE_TTL_MS;
  }

  async function run(): Promise<void> {
    const q = query.trim();
    const ws = wsId;
    // A guest (Connect external) may not read this workspace's roster: never ask, never report.
    if (ws && !workspaceState.current?.external && !formerFresh(ws)) void loadFormer(ws);
    // Operator mid-type (`from:ca`): the avatar typeahead owns the dropdown; no literal search.
    if (typeahead) {
      search.clear();
      return;
    }
    if (q.length < MIN_QUERY_LENGTH || !ws) {
      search.clear();
      return;
    }
    if (sectionFilter) return runSection(ws, q, sectionFilter);
    if (searchingAll) return runAllWorkspaces(ws, q);
    return runWorkspace(ws, q);
  }

  async function runWorkspace(ws: string, q: string): Promise<void> {
    across = null;
    // Scoped search shows no channel rows, so it needs no browse list.
    const needBrowse = !scope && !withinWindow(browse, ws);
    const refusedHere = withinWindow(accessRefused, ws);
    // Soft leg, never rejects: list_channels is closed to an external guest, and a
    // transient failure there must not throw away message hits.
    const browseLeg = needBrowse
      ? listChannels().catch((err) => {
          console.error("Channel list unavailable for search:", err);
          return null;
        })
      : Promise.resolve(null);
    // Only the messages leg may latch a refusal for the whole workspace.
    const outcome = await search.run(
      {
        workspaceId: ws,
        messages: refusedHere ? Promise.resolve<Message[]>([]) : client.searchMessages(ws, q, 50, buildMessageScope()),
        // Scoped search is messages-only (a task/page isn't in a conversation): skip
        // those legs so the Tasks/Docs sections don't render under an `in:` chip.
        tasks: messagesOnly ? Promise.resolve([]) : searchTasks(q),
        pages: messagesOnly ? Promise.resolve([]) : searchPages(q),
        // A Connect guest reads no skills; the whole-workspace bar matches only this workspace's own.
        skills: messagesOnly || workspaceState.current?.external ? undefined : quickSkillsLeg(ws, q),
      },
      { isRefusal: isWorkspaceRefusal, alongside: browseLeg },
    );
    if (!outcome.current) return; // a newer search superseded this one
    const rows = outcome.alongside;
    const refused = refusedHere || outcome.refused;
    if (rows) browse = { wsId: ws, rows, at: Date.now() };
    // A refused or failed list restarts the window (keeping this workspace's last rows),
    // so a caller who may never list channels asks once per window, not once per search.
    else if (needBrowse)
      browse = { wsId: ws, rows: browse?.wsId === ws ? browse.rows : [], at: Date.now() };
    if (refused && !refusedHere) accessRefused = { wsId: ws, at: Date.now() };
    else if (!refused && accessRefused?.wsId === ws) accessRefused = null;
    activeIndex = 0;
  }

  // The Skills leg; whether the marketplaces answered is shown under its rows.
  let skillsMarketFailed = $state(false);
  async function skillsLeg(ws: string, q: string): Promise<SkillSearchHit[]> {
    const r = await searchSkillsLeg(ws, q);
    skillsMarketFailed = r.marketplaceFailed;
    return r.hits;
  }

  function openSkill(hit: SkillSearchHit): void {
    const ws = wsId;
    scopedSearch.record(query, scope, sectionFilter ?? undefined);
    clear();
    if (ws) openSkillHit(ws, hit);
  }

  function seeAllSkills(): void {
    const ws = wsId;
    const q = trimmed;
    scopedSearch.record(query, scope, sectionFilter ?? undefined);
    clear();
    if (ws) openSkillsTarget(ws, { kind: "search", query: q });
  }

  function sectionLegs(ws: string, q: string, only: SearchSectionFilter): SearchLegs {
    if (only === "pages") return { workspaceId: ws, pages: searchPages(q) };
    if (only === "skills") return { workspaceId: ws, skills: skillsLeg(ws, q) };
    return { workspaceId: ws, tasks: searchTasks(q) };
  }

  // `in:Docs`: one leg, no messages, no channel list, no jump rows.
  async function runSection(ws: string, q: string, only: SearchSectionFilter): Promise<void> {
    across = null;
    const outcome = await search.run(sectionLegs(ws, q, only));
    if (!outcome.current) return; // a newer search superseded this one
    activeIndex = 0;
  }

  // One `cyborg:search` per workspace, messages only (a task or a page is opened inside its own
  // workspace). Each relay answers for its own workspace, so its rules decide what shows.
  function once<T>(held: Map<string, Promise<T>>, id: string, load: () => Promise<T>): Promise<T> {
    const had = held.get(id);
    if (had) return had;
    const asked = load();
    held.set(id, asked);
    asked.catch(() => held.delete(id)); // intentional: a failed read is asked again next time
    return asked;
  }

  // The person's own channels plus the public ones they have not joined: a hit can be in either.
  async function workspaceChannels(id: string): Promise<Channel[]> {
    const [mine, unjoined] = await Promise.all([
      client.fetchChannels(id),
      client.listChannels(id).catch((): Channel[] => []), // intentional: closed to a guest, whose own list still names their hits
    ]);
    return [...unjoined, ...mine];
  }

  async function workspaceAgents(id: string): Promise<Array<{ id: string; name: string }>> {
    return (await client.fetchCybos(id)).map((c) => ({ id: c.id, name: c.name }));
  }

  async function workspacePeople(id: string): Promise<Array<{ id: string; name: string }>> {
    const { members, external } = await client.listMembers(id);
    return [
      ...members.map((p) => ({ id: p.userId, name: p.name ?? (memberEmailLocalPart(p) || "User") })),
      ...external.map((p) => ({ id: p.userId, name: p.name ?? "Guest" })),
    ];
  }

  // The unscoped run reads the browse list beside its legs; the all-workspaces run needs it too.
  async function loadBrowse(ws: string): Promise<void> {
    try {
      browse = { wsId: ws, rows: await listChannels(), at: Date.now() };
    } catch (err) {
      // intentional: closed to a guest; their hits keep the names their own channel list gives.
      console.error("Channel list unavailable for search:", err);
    }
  }

  async function runAllWorkspaces(ws: string, q: string): Promise<void> {
    const mine = ++acrossRun;
    if (!withinWindow(browse, ws)) void loadBrowse(ws);
    let found: AllWorkspacesResult | undefined;
    const messages = searchAllWorkspaces(
      workspaceState.list.map((w) => ({ id: w.id, name: w.name })),
      ws,
      {
        // The typed and chip filters (from:, has:, mentions:) narrow every workspace's search too.
        search: (id) => client.searchMessages(id, q, ALL_WORKSPACES_LIMIT, buildMessageScope()),
        channels: (id) => once(channelLists, id, () => workspaceChannels(id)),
        people: (id) => once(peopleLists, id, () => workspacePeople(id)),
        agents: (id) => once(agentLists, id, () => workspaceAgents(id)),
        isRefusal: isWorkspaceRefusal,
        stale: () => mine !== acrossRun,
      },
    ).then((out) => {
      found = out;
      return out.messages;
    });
    const outcome = await search.run({
      workspaceId: ws,
      messages,
      tasks: Promise.resolve([]),
      pages: Promise.resolve([]),
    });
    if (!outcome.current) return; // a newer search superseded this one
    across = found ?? null;
    activeIndex = 0;
  }

  function toggleAllWorkspaces(): void {
    allWorkspaces = !allWorkspaces;
    activeIndex = 0;
    if (debounce) clearTimeout(debounce);
    void run();
    inputEl?.focus();
  }

  let debounce: ReturnType<typeof setTimeout> | null = null;
  function onInput(e?: Event): void {
    absorbTypedFilters((e as InputEvent | undefined)?.inputType === "insertFromPaste");
    open = true;
    activeIndex = 0;
    if (debounce) clearTimeout(debounce);
    acrossRun++;
    search.supersede();
    debounce = setTimeout(() => void run(), 250);
  }

  function retry(): void {
    if (debounce) clearTimeout(debounce);
    void run();
    inputEl?.focus();
  }

  // #689: navigate to the result's real destination — public/private/group-DM
  // channels via /channel/, a 1-1 human DM via /dm/, an agent/cybo DM via
  // /agent/ — each carrying `?m=<id>` so the target route focuses + scrolls to
  // the exact matched message (the SAME deep-link the channel/dm pages consume).
  function pick(href: string | null): void {
    if (!href) return;
    scopedSearch.record(query, scope, sectionFilter ?? undefined);
    open = false;
    query = "";
    search.clear();
    clearScope();
    goto(href);
  }

  function openChannel(hit: ChannelHit): void {
    if (!wsId) return;
    if (!hit.isMember) {
      previewChannel(hit.channel);
      return;
    }
    channelState.activeId = hit.channel.id;
    pick(`/workspace/${wsId}/channel/${hit.channel.id}`);
  }

  // Same read-only peek as Browse channels: seed the preview, then carry ?preview=1.
  function previewChannel(channel: Channel): void {
    if (!wsId) return;
    void selectChannelPreview(channel);
    pick(`/workspace/${wsId}/channel/${channel.id}?preview=1`);
  }

  async function join(hit: ChannelHit): Promise<void> {
    const ws = wsId;
    if (!ws || joiningId) return;
    const channel = hit.channel;
    joiningId = channel.id;
    try {
      await joinChannel(channel);
      channelState.activeId = channel.id;
      pick(`/workspace/${ws}/channel/${channel.id}`);
    } catch (err) {
      console.error("Failed to join channel:", err);
      reportUiFailure("message_search.join", err, { workspaceId: wsId });
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : `Couldn't join #${channel.name}. Please try again.`,
      );
    } finally {
      joiningId = null;
    }
  }

  function openPerson(hit: PersonHit): void {
    if (!wsId) return;
    channelState.activeId = null;
    pick(`/workspace/${wsId}/${hit.kind === "dm" ? "dm" : "agent"}/${hit.id}`);
  }

  function activate(option: SearchOption): void {
    if (option.kind === "suggest") pickSuggestion(option.suggest);
    else if (option.kind === "show-results") inputEl?.focus();
    else if (option.kind === "recent") applyRecent(option.recent);
    else if (option.kind === "channel") openChannel(option.hit);
    else if (option.kind === "person") openPerson(option.hit);
    else if (option.kind === "message") pick(viewFor(option.message).href);
    else if (option.kind === "skill") openSkill(option.hit);
    // A task/page row is a real link (its component owns the href and closes the bar on
    // click), so Enter takes the same path as a mouse click (selectRow records it).
    else {
      listEl?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.click();
    }
  }

  function selectRow(): void {
    scopedSearch.record(query, scope, sectionFilter ?? undefined);
    clear();
  }

  const peopleCandidateKind = (id: string): SearchScopeKind =>
    peopleCandidates.find((p) => p.id === id)?.kind === "agent" ? "agent" : "dm";

  function addFromPerson(id: string): void {
    if (!fromPeople.some((p) => p.id === id))
      fromPeople = [...fromPeople, { id, label: scopeLabel(peopleCandidateKind(id), id) }];
  }

  function addMentionPerson(id: string): void {
    if (!mentionPeople.some((p) => p.id === id))
      mentionPeople = [...mentionPeople, { id, label: scopeLabel(peopleCandidateKind(id), id) }];
  }

  // Complete a typed operator from the avatar list: drop the half-typed `from:ca`, apply the
  // picked value as its chip/toggle.
  function pickSuggestion(s: Suggestion): void {
    const op = typeahead?.op;
    query = query.slice(0, typeahead?.start ?? query.length);
    if (s.kind === "has") {
      if (s.value === "link") hasLink = true;
      else hasFile = true;
    } else if (s.kind === "section") {
      applySection(s.section);
    } else if (s.kind === "me") {
      if (op === "from") fromMe = true;
      else mentionsMe = true;
    } else if (s.kind === "channel") {
      setScope({ kind: "channel", id: s.id });
    } else if (op === "in") {
      setScope({ kind: s.agent ? "agent" : "dm", id: s.id });
    } else if (s.kind === "person" && s.id === selfId) {
      // You are the me-filter, not a from/mention id.
      if (op === "from") fromMe = true;
      else mentionsMe = true;
    } else if (op === "from") {
      addFromPerson(s.id);
    } else {
      addMentionPerson(s.id);
    }
    filterNotice = null;
    activeIndex = 0;
    queueMicrotask(() => {
      inputEl?.focus();
      const at = query.length;
      inputEl?.setSelectionRange(at, at);
    });
    void run();
  }

  function clear(): void {
    query = "";
    search.clear();
    open = false;
    clearScope();
  }

  function clearScope(): void {
    scope = null;
    sectionFilter = null;
    fromMe = false;
    mentionsMe = false;
    hasLink = false;
    hasFile = false;
    fromPeople = [];
    mentionPeople = [];
    filterNotice = null;
  }

  // The `in:<place>` chip + filter toggles as the `cyborg:search` extras (undefined when
  // unscoped, so an unscoped search is byte-for-byte the original request).
  function buildMessageScope(): MessageSearchScope | undefined {
    const filters = {
      ...(fromMe ? { fromMe: true } : {}),
      // The relay's field is `mentions` (CyborgSearchSchema); `mentionsMe` was silently ignored.
      // Mentions me is a channel / whole-workspace filter: hidden (so never sent) in a DM scope.
      ...(mentionsMe && (!scope || scope.kind === "channel") ? { mentions: true } : {}),
      ...(hasLink ? { hasLink: true } : {}),
      // has:file → the relay's hasFile; ignored if unsupported.
      ...(hasFile ? { hasFile: true } : {}),
      ...(fromPeople.length > 0 ? { fromIds: fromPeople.map((p) => p.id) } : {}),
      // mentions:@id — sent as mentionIds; the relay support is Bursera's.
      ...(mentionPeople.length > 0 ? { mentionIds: mentionPeople.map((p) => p.id) } : {}),
    };
    if (!scope) return Object.keys(filters).length > 0 ? filters : undefined;
    return { ...(scope.kind === "channel" ? { channelId: scope.id } : { dmPeerId: scope.id }), ...filters };
  }

  type Resolved = { kind: SearchScopeKind; id: string } | { notice: string };

  // A typed `#name` / `@name` resolved against the roster the bar already holds (the same ids the
  // header Search button passes); a miss or an ambiguous name says so instead of searching unfiltered.
  function resolveTyped(sigil: "#" | "@", name: string): Resolved {
    if (sigil === "#") {
      const c = workspaceState.channels.find((ch) => nameKey(ch.name ?? "") === nameKey(name));
      return c ? { kind: "channel", id: c.id } : { notice: `No channel named #${name}` };
    }
    // Live people first: a removed "Alice Jones" never makes a live "Alice" ambiguous.
    const live = resolveName(name, livePeople);
    const r =
      "missing" in live ? resolveName(name, peopleCandidates.filter((p) => p.gone)) : live;
    if ("match" in r) return { kind: r.match.kind === "agent" ? "agent" : "dm", id: r.match.id };
    if ("ambiguous" in r) return { notice: `@${name} matches ${r.ambiguous} people — type in:"@Full Name"` };
    return { notice: `No person or agent named @${name}` };
  }

  // Why a typed filter can't apply (an unknown/ambiguous name, an unsupported value), or undefined.
  function filterNoticeFor(t: PositionedToken): string | undefined {
    if (t.kind === "unsupported") return `${t.raw} isn't a search filter yet`;
    if (t.kind !== "in" && t.kind !== "from" && t.kind !== "mentions") return undefined;
    const r = resolveTyped(t.kind === "in" ? t.sigil : "@", t.name);
    return "notice" in r ? r.notice : undefined;
  }

  // Apply one resolvable filter through the button's setters; false when it can't apply.
  // Every typed filter narrows messages, so it widens an `in:Docs` search back to messages.
  function applyTypedFilter(t: PositionedToken): boolean {
    // `in:Docs` / `in:Tasks`: pin to the section instead of widening back to messages.
    if (t.kind === "section") {
      applySection(t.section);
      return true;
    }
    const applied = applyMessageFilter(t);
    if (applied) sectionFilter = null;
    return applied;
  }

  function applyMessageFilter(t: PositionedToken): boolean {
    if (t.kind === "in" || t.kind === "from" || t.kind === "mentions") {
      const r = resolveTyped(t.kind === "in" ? t.sigil : "@", t.name);
      if ("notice" in r) return false;
      if (t.kind === "in") setScope(r);
      else if (t.kind === "from") addFromPerson(r.id);
      else addMentionPerson(r.id);
      return true;
    }
    if (t.kind === "fromMe") fromMe = true;
    else if (t.kind === "hasLink") hasLink = true;
    else if (t.kind === "hasFile") hasFile = true;
    else if (t.kind === "mentionsMe") mentionsMe = true;
    return t.kind !== "unsupported";
  }

  // Keep the caret where the user was typing, minus what was absorbed in front of it.
  function restoreCaret(caret: number, absorbed: PositionedToken[]): void {
    const removed = absorbed.filter((t) => t.end < caret).reduce((n, t) => n + (t.end - t.start + 1), 0);
    const at = Math.max(0, Math.min(query.length, caret - removed));
    queueMicrotask(() => inputEl?.setSelectionRange(at, at));
  }

  // Typed filters become the same chips the header button sets (one path: setScope + the toggles).
  // While typing only finished words before the caret count; paste / Enter take the whole text.
  // Only absorbed filters leave the text: everything else stays exactly as typed. True if any did.
  function absorbTypedFilters(whole = false): boolean {
    const caret = whole ? undefined : (inputEl?.selectionEnd ?? query.length);
    const absorbed = parseSearchQuery(query, caret).filter(applyTypedFilter);
    // Notices cover every filter left in the text, not just the finished ones before the caret.
    // ...except the word still being typed at the caret (no "@B", "@Bu"… flashes).
    const notices = parseSearchQuery(query)
      .filter((t) => !absorbed.some((a) => a.start === t.start))
      .filter((t) => caret === undefined || t.end < caret || t.start > caret)
      .map(filterNoticeFor)
      .filter((n): n is string => n !== undefined);
    filterNotice = notices.length > 0 ? notices.join(" · ") : null;
    if (absorbed.length === 0) return false;
    query = removeTokens(query, absorbed);
    if (caret !== undefined) restoreCaret(caret, absorbed);
    return true;
  }

  // The chip label for a place, resolved from the roster the bar already holds.
  function scopeLabel(kind: SearchScopeKind, id: string): string {
    if (kind === "channel") {
      const c = channelsById.get(id);
      if (!c) return "#channel";
      return c.isPrivate ? c.name : `#${c.name}`;
    }
    const name = messagePeerName(id) ?? formerById.get(id)?.name;
    if (name) return `@${name}`;
    return kind === "agent" ? "@agent" : "@user";
  }

  // Pin the bar to a place: the header magnifier and ⌘F both land here.
  function setScope(req: { kind: SearchScopeKind; id: string }): void {
    scope = { kind: req.kind, id: req.id, label: scopeLabel(req.kind, req.id) };
    sectionFilter = null;
  }

  function applyScope(req: { kind: SearchScopeKind; id: string }): void {
    setScope(req);
    fromPeople = [];
    mentionPeople = [];
    fromMe = false;
    mentionsMe = false;
    hasLink = false;
    hasFile = false;
    openFiltered();
  }

  function applySection(only: SearchSectionFilter): void {
    clearScope();
    sectionFilter = only;
    openFiltered();
  }

  function openFiltered(): void {
    open = true;
    activeIndex = 0;
    queueMicrotask(() => {
      inputEl?.focus();
      inputEl?.select();
    });
      // The opening click still bubbles to onDocClick (which closes the bar): re-open after it.
    setTimeout(() => {
      if (scope || sectionFilter) open = true;
    }, 0);
    void run();
  }

  function applyRecent(r: RecentSearch): void {
    scope = r.scope;
    sectionFilter = r.scope ? null : (r.section ?? null);
    if (!r.scope) {
      fromPeople = [];
      mentionPeople = [];
      fromMe = false;
      mentionsMe = false;
      hasLink = false;
      hasFile = false;
    }
    query = r.query;
    open = true;
    activeIndex = 0;
    if (debounce) clearTimeout(debounce);
    void run();
    inputEl?.focus();
  }

  // The place the user is currently viewing — what ⌘F scopes to when pressed inside a
  // channel, DM or agent conversation (the header Search button passes its place explicitly).
  const currentPlace = $derived.by<{ kind: SearchScopeKind; id: string } | null>(() => {
    const path = page.url?.pathname ?? "";
    const p = page.params ?? {};
    if (p.channelId && path.includes("/channel/")) return { kind: "channel", id: p.channelId };
    if (p.userId && path.includes("/dm/")) return { kind: "dm", id: p.userId };
    if (p.agentId && path.includes("/agent/")) return { kind: "agent", id: p.agentId };
    return null;
  });

  // A header Search button (owned by Sprite) opens the bar through openScopedSearch(),
  // which bumps scopedSearch.request; apply it here. The effect subscribes ONLY to
  // `request`; applyScope (and the search it kicks off) read + write other state, so they
  // run untracked — otherwise the effect would re-trigger on its own writes.
  $effect(() => {
    const req = scopedSearch.request;
    if (!req) return;
    untrack(() => {
      applyScope({ kind: req.kind, id: req.id });
      // Consume it: the controller is a module singleton and MessageSearch unmounts /
      // remounts on every workspace switch (showTitlebarSearch ← workspaceReady). Left set,
      // a stale request would replay on the next mount and hijack the bar in another
      // workspace. Cleared under untrack so this write doesn't loop the effect.
      scopedSearch.request = null;
    });
  });

  $effect(() => {
    const req = scopedSearch.sectionRequest;
    if (!req) return;
    untrack(() => {
      applySection(req.section);
      scopedSearch.sectionRequest = null;
    });
  });

  function scrollActiveIntoView(): void {
    queueMicrotask(() => {
      listEl?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.scrollIntoView?.({ block: "nearest" });
    });
  }

  // Drop one filter (its chip's × or Backspace); the others stay.
  function removeFilter(key: ActiveFilterKey): void {
    if (key.kind === "in") {
      scope = null;
      sectionFilter = null;
    } else if (key.kind === "from") fromPeople = fromPeople.filter((p) => p.id !== key.id);
    else if (key.kind === "mentions") mentionPeople = mentionPeople.filter((p) => p.id !== key.id);
    else if (key.kind === "fromMe") fromMe = false;
    else if (key.kind === "mentionsMe") mentionsMe = false;
    else if (key.kind === "hasLink") hasLink = false;
    else if (key.kind === "hasFile") hasFile = false;
    else allWorkspaces = false;
    filterNotice = null;
    activeIndex = 0;
    if (debounce) clearTimeout(debounce);
    void run();
    inputEl?.focus();
  }

  // Backspace on an empty field removes the last chip in the bar.
  function eatLastChip(): boolean {
    const last = barFilters.at(-1);
    if (!last) return false;
    removeFilter(last.key);
    return true;
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      // Esc closes the typeahead list without wiping the query; otherwise it clears the bar.
      if (typeaheadActive) {
        e.preventDefault();
        open = false;
        return;
      }
      clear();
      return;
    }
    if (e.key === "Backspace" && query === "" && eatLastChip()) {
      e.preventDefault();
      return;
    }
    // No MIN gate here: the scoped empty-state (Show results for + recents) is keyboard-
    // navigable too. ArrowUp/Down and Enter self-guard on options.length below, which is 0
    // in the only case the dropdown is hidden (unscoped, below MIN).
    if (e.isComposing || !open) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (options.length === 0) return;
      e.preventDefault();
      const delta = e.key === "ArrowDown" ? 1 : -1;
      activeIndex = (activeIndex + delta + options.length) % options.length;
      scrollActiveIntoView();
    } else if (e.key === "Tab" && typeaheadActive) {
      // Tab completes the highlighted suggestion into its chip.
      e.preventDefault();
      const option = options[activeIndex];
      if (option) activate(option);
    } else if (e.key === "Enter") {
      // In the typeahead, Enter picks the highlighted suggestion.
      if (typeaheadActive) {
        e.preventDefault();
        const option = options[activeIndex];
        if (option) activate(option);
        return;
      }
      // A filter still being typed (`in:#general` + Enter) becomes its chip first; if nothing new
      // is absorbed, Enter opens the highlighted result as usual.
      if (absorbTypedFilters(true)) {
        e.preventDefault();
        void run();
        return;
      }
      const option = options[activeIndex];
      if (!option) return;
      e.preventDefault();
      activate(option);
    }
  }

  // Focus only — never opens the dropdown. Opening is an explicit act (a click), so the
  // app programmatically focusing the bar (or a plain focus) never pops the panel open.
  function focusSearch(): void {
    if (!inputEl?.isConnected) return;
    inputEl.focus();
    inputEl.select();
  }

  // The headers' Search buttons land here (header-search.ts).
  $effect(() => registerTopSearch(focusSearch));

  // Mod+F focuses the global search from anywhere (documented in
  // keyboard-shortcuts.ts as "Search messages" but never wired up until now).
  // preventDefault suppresses the browser's native find so the shortcut always
  // means "search messages & tasks" — matching QuickSwitcher's Mod+K guard shape
  // (no Alt/Shift) but WITHOUT the editable-target bail: unlike the palette,
  // jumping to search from a focused composer is the whole point.
  // ⌘K is left to the QuickSwitcher (CYBORG-1093): the search card opens only on a click.
  function onWindowKeyDown(e: KeyboardEvent): void {
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === "f" || e.key === "F")) {
      e.preventDefault();
      // ⌘F inside a place searches THAT place first; elsewhere (or already pinned to it)
      // it just focuses the bar. Clearing the chip then falls back to the workspace.
      const place = currentPlace;
      if (place && scope?.id !== place.id) applyScope(place);
      // On Agents → Skills, ⌘F searches skills (workspace and marketplaces).
      else if (!place && sectionFilter !== "skills" && onSkillsPlace(page.url?.pathname ?? "")) applySection("skills");
      else focusSearch();
    }
  }

  function onDocClick(e: MouseEvent): void {
    // composedPath() is the dispatch-time path, so a "Show N more" click still lists
    // containerEl even though expanding its section detaches the clicked button before
    // this handler runs; contains() would miss the detached node and wrongly close the bar.
    if (containerEl && !e.composedPath().includes(containerEl)) open = false;
  }

  function memberLabel(n: number): string {
    return `${n} ${n === 1 ? "member" : "members"}`;
  }

  function channelSubline(hit: ChannelHit): string {
    const parts: string[] = [];
    if (hit.memberCount !== null && !hit.isMember) parts.push(memberLabel(hit.memberCount));
    if (hit.channel.description) parts.push(hit.channel.description);
    return parts.join(" · ");
  }
</script>

{#snippet showMore(hidden: number, expand: () => void)}
  <button
    type="button"
    onclick={expand}
    class="w-full px-4 py-1.5 pl-10 text-left text-xs font-medium text-content-muted hover:text-content cursor-pointer"
  >
    Show {hidden} more
  </button>
{/snippet}

{#snippet messagesSection()}
  {#if search.messagesFailed}
    <!-- The messages leg failed on its own: the other sections still render. -->
    {@render sectionLabel("Messages", null)}
    <div class="flex items-center gap-3 px-4 py-3">
      <CircleAlertIcon class="size-4 shrink-0 text-error" aria-hidden="true" />
      <div role="alert" class="min-w-0 flex-1">
        <p class="text-sm font-medium text-content">Couldn't search messages</p>
        <p class="text-caption text-content-dim">Check your connection and try again.</p>
      </div>
      <Button variant="outline" size="sm" onclick={retry}>
        <RefreshCwIcon aria-hidden="true" />
        Try again
      </Button>
    </div>
  {:else if search.results.length > 0}
    {@render sectionLabel("Messages", search.results.length)}
    {#each visibleMessages as m, i (m.id)}
      {@const idx = sectionStart.messages + i}
      {@const view = viewFor(m)}
      {@const relayed = relayedRowOf(m)}
      {#if searchingAll && m.workspaceId !== visibleMessages[i - 1]?.workspaceId}
        <!-- One group per workspace: its rows open in that workspace. -->
        <div data-testid="search-workspace-group" class="border-b border-edge bg-raised px-4 py-1 text-xs font-medium text-content-dim">
          {workspaceName(m.workspaceId)}
        </div>
      {/if}
      <button
        type="button"
        data-index={idx}
        onclick={() => pick(view.href)}
        onmousemove={() => (activeIndex = idx)}
        data-result-kind={view.kind}
        class={cn("block w-full border-b border-edge px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-raised cursor-pointer", activeIndex === idx && "bg-raised")}
      >
        <div class="mb-1 flex items-center gap-2">
          <span class="flex items-center gap-1 text-xs text-content-dim">
            {#if view.kind === "public"}
              <ChannelGlyph kind="hash" class="w-3 h-3 shrink-0 text-content-muted" />
            {:else if view.kind === "private"}
              <ChannelGlyph kind="private" class="w-3 h-3 shrink-0 text-content-muted" />
            {:else if view.kind === "group_dm"}
              <!-- #608 multi-person glyph for a group DM -->
              <svg class="w-3 h-3 shrink-0 text-content-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            {:else}
              <!-- 1-1 or agent/cybo DM: avatar + peer name; a removed person greyed, as Slack does -->
              {@const gonePeer = view.kind === "dm" ? peerGone(m, view.peerId) : null}
              <span class={cn("inline-flex", gonePeer && "opacity-50 grayscale")}><ActivityActorAvatar actorId={view.peerId ?? null} actorName={view.name} width={14} fontSize={8} /></span>
              {view.name}{#if gonePeer}{" "}<span class="text-content-muted" data-testid="search-gone">({gonePeer})</span>{/if}
            {/if}
            {#if view.kind !== "dm"}{view.name}{/if}
          </span>
          <span class="text-xs text-content-muted">·</span>
          {#if relayed}
            <!-- Sent by a session or an agent for the person: named as its sender. -->
            <span class="text-xs font-medium text-content-dim" data-testid="search-relayed-name">{relayed.name}</span>
            <span class="rounded bg-accent/15 px-1 text-micro font-medium text-accent">{RELAYED_CHIP[relayed.kind]}</span>
            <span class="min-w-0 truncate text-xs text-content-muted">· on behalf of {liveHumanName(m, workspaceState.members)}</span>
          {:else}
            <span class="text-xs font-medium text-content-dim">{liveHumanName(m, workspaceState.members)}{#if authorGone(m)}{" "}<span class="font-normal text-content-muted" data-testid="search-gone">({authorGone(m)})</span>{/if}</span>
          {/if}
          {#if m.fromType === "agent"}
            {@const bot = externalAuthor(m.fromId, cyboState.list)}
            <span class="rounded bg-accent/15 px-1 text-micro font-medium text-accent">Agent</span>
            {#if bot}<ExternalBotMark mark={bot.mark} name={bot.name} />{/if}
          {/if}
          <span class="ml-auto shrink-0 text-xs text-content-muted">{formatMessageTimestamp(m.createdAt)}</span>
        </div>
        <div class="line-clamp-2 text-sm text-content">
          {@render highlighted(typeof m.text === "string" ? m.text : "")}
        </div>
      </button>
    {/each}
    {#if search.results.length > visibleMessages.length}
      {@render showMore(search.results.length - visibleMessages.length, () => (search.messagesExpanded = true))}
    {/if}
  {/if}
  {#if acrossFailed && across && !search.loading}
    <!-- A workspace that could not be searched is said, never passed off as "no results" there. -->
    <div role="alert" class="px-4 py-2 text-xs text-content-dim">
      Couldn't search {across.failed.map((w) => w.name).join(", ")}
    </div>
  {/if}
{/snippet}

{#snippet tasksSection()}
  {#if wsId && search.tasksFailed}
    <!-- The tasks leg failed on its own. Hiding the section would read as
         "no tasks matched", so say what actually happened. -->
    {@render sectionLabel("Tasks", null)}
    <div role="alert" class="px-4 py-3 text-sm text-content-dim">Couldn't search tasks</div>
  {:else if wsId && search.taskResults.length > 0}
    {@render sectionLabel("Tasks", search.taskResults.length)}
    <TaskSearchResults
      results={search.visibleTasks}
      query={trimmed}
      workspaceId={wsId}
      startIndex={sectionStart.tasks}
      {activeIndex}
      onhover={(i) => (activeIndex = i)}
      onselect={selectRow}
    />
    {#if search.taskResults.length > search.visibleTasks.length}
      {@render showMore(search.taskResults.length - search.visibleTasks.length, () => (search.tasksExpanded = true))}
    {/if}
  {/if}
{/snippet}

{#snippet pagesSection()}
  {#if wsId && search.pagesFailed}
    {@render sectionLabel("Docs", null)}
    <div role="alert" class="px-4 py-3 text-sm text-content-dim">Couldn't search pages</div>
  {:else if wsId && search.pageResults.length > 0}
    {@render sectionLabel("Docs", search.pageResults.length)}
    <PageSearchResults
      results={search.visiblePages}
      query={trimmed}
      workspaceId={wsId}
      startIndex={sectionStart.pages}
      {activeIndex}
      onhover={(i) => (activeIndex = i)}
      onselect={selectRow}
    />
    {#if search.pageResults.length > search.visiblePages.length}
      {@render showMore(search.pageResults.length - search.visiblePages.length, () => (search.pagesExpanded = true))}
    {/if}
  {/if}
{/snippet}

{#snippet skillsSection()}
  {#if wsId && search.skillsFailed}
    {@render sectionLabel("Skills", null)}
    <div role="alert" class="px-4 py-3 text-sm text-content-dim">Couldn't search skills</div>
  {:else if wsId && (search.skillResults.length > 0 || sectionFilter === "skills")}
    {@render sectionLabel("Skills", search.skillResults.length)}
    <SkillSearchResults
      results={search.visibleSkills}
      query={trimmed}
      startIndex={sectionStart.skills}
      {activeIndex}
      onhover={(i) => (activeIndex = i)}
      onselect={openSkill}
    />
    {#if search.skillResults.length > search.visibleSkills.length}
      {@render showMore(search.skillResults.length - search.visibleSkills.length, () => (search.skillsExpanded = true))}
    {/if}
    {#if sectionFilter === "skills"}
      {#if skillsMarketFailed}
        <p role="status" class="px-4 py-2 text-xs text-content-dim">The marketplaces didn't answer; showing this workspace's skills.</p>
      {/if}
      <button type="button" onclick={seeAllSkills} class="w-full cursor-pointer px-4 py-2 text-left text-xs font-medium text-content-dim hover:text-content">
        See every marketplace result in Discover →
      </button>
      <p class="px-4 pb-2 text-xs text-content-dim">Remove “in Skills” to search messages, tasks and docs too.</p>
    {/if}
  {/if}
{/snippet}

<svelte:document onclick={onDocClick} />
<svelte:window onkeydown={onWindowKeyDown} />

{#snippet sectionLabel(label: string, count: number | null)}
  <div class="flex items-center gap-1.5 px-4 pb-1 pt-3 text-micro font-semibold uppercase tracking-wide text-content-muted">
    <!-- A null count is a section that failed: no number, because we don't have one. -->
    {label} {#if count !== null}<span class="tabular-nums opacity-70">{count}</span>{/if}
  </div>
{/snippet}

{#snippet highlighted(text: string)}
  {@const parts = highlightParts(text, trimmed)}
  {parts[0]}{#if parts[1]}<mark class="rounded-sm bg-warning/30 px-0.5 text-content">{parts[1]}</mark>{/if}{parts[2]}
{/snippet}

{#snippet filterChip(label: string, active: boolean, toggle: () => void)}
  <button
    type="button"
    onclick={toggle}
    aria-pressed={active}
    class={cn(
      "inline-flex h-[26px] shrink-0 items-center gap-1 rounded-full border px-2.5 text-xs transition-colors cursor-pointer",
      active ? "border-accent bg-accent/15 text-accent" : "border-edge text-content hover:bg-raised",
    )}
  >
    {label}{#if active}<span aria-hidden="true">✕</span>{/if}
  </button>
{/snippet}

<!-- Scope + filter toggles (CYBORG-search-ui). Only while pinned to a place; renders
     nothing (so the unscoped bar is unchanged) otherwise. Mentions me is channel-only. -->
{#snippet filterChips()}
  <!-- Message filters don't apply to a section (`in:Docs`). -->
  {#if !sectionFilter}
  <div class="flex flex-wrap items-center gap-1.5 border-b border-edge px-2.5 py-2" data-testid="search-filter-chips">
    {#if scope}
      <button
        type="button"
        onclick={() => removeFilter({ kind: "in" })}
        class="inline-flex h-[26px] shrink-0 items-center gap-1.5 rounded-full border border-accent bg-accent/15 px-2.5 text-xs text-accent cursor-pointer"
      >
        {#if scope.kind === "channel"}<ChannelGlyph kind={scopePrivate ? "private" : "hash"} class="w-3 h-3" />{/if}
        {scope.kind === "channel" ? "In this channel" : "In this conversation"}
      </button>
    {/if}
    {#each fromPeople as person (person.id)}
      <button
        type="button"
        data-testid="search-from-chip"
        onclick={() => { fromPeople = fromPeople.filter((p) => p.id !== person.id); void run(); }}
        aria-label={`Remove from:${person.label}`}
        class="inline-flex h-[26px] shrink-0 items-center gap-1.5 rounded-full border border-accent bg-accent/15 px-2.5 text-xs text-accent cursor-pointer"
      >from:{person.label} ×</button>
    {/each}
    {#each mentionPeople as person (person.id)}
      <button
        type="button"
        data-testid="search-mentions-chip"
        onclick={() => { mentionPeople = mentionPeople.filter((p) => p.id !== person.id); void run(); }}
        aria-label={`Remove mentions:${person.label}`}
        class="inline-flex h-[26px] shrink-0 items-center gap-1.5 rounded-full border border-accent bg-accent/15 px-2.5 text-xs text-accent cursor-pointer"
      >mentions:{person.label} ×</button>
    {/each}
    {@render filterChip("From me", fromMe, () => { fromMe = !fromMe; void run(); })}
    {#if !scope || scope.kind === "channel"}
      {@render filterChip("Mentions me", mentionsMe, () => { mentionsMe = !mentionsMe; void run(); })}
    {/if}
    {@render filterChip("Has a link", hasLink, () => { hasLink = !hasLink; void run(); })}
    {@render filterChip("Has a file", hasFile, () => { hasFile = !hasFile; void run(); })}
    {#if !scope && canSearchAll}
      {@render filterChip("All workspaces", allWorkspaces, toggleAllWorkspaces)}
    {/if}
  </div>
  {/if}
  {#if filterNotice}
    <p class="m-0 border-b border-edge px-3 py-1.5 text-caption text-content-muted" data-testid="search-filter-notice">{filterNotice}</p>
  {/if}
{/snippet}

<!-- One recent-search row (shared by the scoped and unscoped empty states). -->
{#snippet recentRow(r: RecentSearch, idx: number)}
  <button
    type="button"
    data-index={idx}
    data-testid="search-recent"
    onclick={() => applyRecent(r)}
    onmousemove={() => (activeIndex = idx)}
    class={cn("flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left cursor-pointer hover:bg-raised", activeIndex === idx && "bg-raised")}
  >
    <HistoryIcon class="w-4 h-4 shrink-0 text-content-muted" aria-hidden="true" />
    <span class="min-w-0 truncate text-sm text-content">
      {r.query}{#if r.scope}<span class="text-caption text-content-dim">{" "}in {r.scope.label}</span>{:else if r.section}<span class="text-caption text-content-dim">{" "}in {SECTION_FILTER_LABEL[r.section]}</span>{/if}
    </span>
  </button>
{/snippet}

<!-- A compact filter button in the empty card's 2×2 grid: taps it to start typing that operator. -->
{#snippet gridFilter(op: string, hint: string)}
  <button
    type="button"
    data-testid="search-grid-filter"
    onclick={() => { query = `${op}:`; open = true; activeIndex = 0; inputEl?.focus(); }}
    class="flex min-w-0 items-center gap-2 rounded-md border border-edge px-2.5 py-1.5 text-left text-sm text-content hover:bg-raised cursor-pointer"
  >
    <span class="shrink-0 font-medium text-accent">{op}:</span>
    <span class="min-w-0 truncate text-caption text-content-dim">{hint}</span>
  </button>
{/snippet}

<!-- The scoped / section bar before anything is typed: a "Show results for" affordance plus
     recent searches. Typing replaces this with the scoped results. -->
{#snippet scopedEmpty()}
  {#if filterLabel}
    <!-- Index 0: the affordance. It echoes the active filter and focuses the field to type. -->
    <button
      type="button"
      data-index={0}
      onclick={() => inputEl?.focus()}
      onmousemove={() => (activeIndex = 0)}
      class={cn("flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm text-content cursor-pointer hover:bg-raised", activeIndex === 0 && "bg-raised")}
    >
      <svg class="w-4 h-4 shrink-0 text-content-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      <span>Show results for <span class="font-medium text-accent">in:{filterLabel}</span></span>
    </button>
  {/if}
  {#if shownRecents.length > 0}
    {@render sectionLabel("Recent searches", null)}
    <div class="px-1.5">
      {#each shownRecents as r, i (r.query + ":" + (r.scope?.id ?? r.section ?? ""))}
        {@render recentRow(r, i + 1)}
      {/each}
    </div>
  {/if}
{/snippet}

<!-- The unscoped bar before anything is typed (CYBORG-search-typeahead): up to RECENTS_SHOWN
     recents, then the four filters in a 2×2 grid. Tapping a filter seeds its operator. -->
{#snippet unscopedCard()}
  {#if shownRecents.length > 0}
    {@render sectionLabel("Recent searches", null)}
    <div class="px-1.5">
      {#each shownRecents as r, i (r.query + ":" + (r.scope?.id ?? r.section ?? ""))}
        {@render recentRow(r, i)}
      {/each}
    </div>
  {/if}
  {@render sectionLabel("Filters", null)}
  <div class="grid grid-cols-2 gap-1.5 px-3 pb-2" data-testid="search-filter-grid">
    {@render gridFilter("from", "person · agent")}
    {@render gridFilter("in", "channel · DM · agent")}
    {@render gridFilter("has", "link · file")}
    {@render gridFilter("mentions", "me · @person")}
  </div>
{/snippet}

<!-- Operator/value typeahead: a letter-filtered list of people, agents, channels (or has:
     options) with avatars; ↑↓ to move, Enter/Tab to pick, Esc to close. -->
{#snippet typeaheadList()}
  <div class="px-1.5 pt-1">
    {#each suggestions as s, i (s.kind === "has" ? `has:${s.value}` : s.kind === "me" ? "me" : s.kind === "section" ? `section:${s.section}` : `${s.kind}:${s.id}`)}
      <button
        type="button"
        data-index={i}
        data-testid="search-suggestion"
        onclick={() => pickSuggestion(s)}
        onmousemove={() => (activeIndex = i)}
        class={cn("flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left cursor-pointer hover:bg-raised", activeIndex === i && "bg-raised")}
      >
        {#if s.kind === "person"}
          <span class={cn("inline-flex", s.gone && "opacity-50 grayscale")}><ActivityActorAvatar actorId={s.id} actorName={s.name} width={20} fontSize={10} isAgent={s.agent} /></span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium text-content">{s.name}{#if s.gone}{" "}<span class="font-normal text-content-muted" data-testid="search-gone">({s.gone})</span>{/if}</span>
            {#if s.email}<span class="block truncate text-caption text-content-dim">{s.email}</span>{/if}
          </span>
          {#if s.agent}
            {@const bot = externalAuthor(s.id, cyboState.list)}
            <span class="shrink-0 rounded bg-accent/15 px-1 text-micro font-medium text-accent">Agent</span>
            {#if bot}<ExternalBotMark mark={bot.mark} name={bot.name} />{/if}
          {/if}
        {:else if s.kind === "channel"}
          <span class="flex w-5 shrink-0 items-center justify-center text-content-muted"><ChannelGlyph kind={s.isPrivate ? "private" : "hash"} class="w-3.5 h-3.5" /></span>
          <span class="min-w-0 flex-1 truncate text-sm font-medium text-content">{s.name}</span>
        {:else}
          <span class="flex w-5 shrink-0 items-center justify-center text-content-muted">
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <span class="min-w-0 flex-1 truncate text-sm text-content">{s.label}</span>
        {/if}
      </button>
    {/each}
  </div>
{/snippet}

<!-- Width + centering live HERE, not on the pill below: the dropdown
     (`absolute left-0 right-0`, further down) positions against THIS element,
     so the pill and its dropdown must share the same box or their edges
     mismatch. The pill is a plain `w-full` that just fills this box.

     data-no-drag also lives HERE, not on the +layout.svelte wrapper this
     component is mounted in. `-webkit-app-region: no-drag` (app.css
     `.drag-region [data-no-drag]`) carves a no-drag hole matching the
     ATTRIBUTE-CARRYING element's own rendered box, not its narrowest
     descendant. This div is the one with the real width clamp
     (sm:max-w-[40rem] etc.) — the only element whose box equals the visible
     pill at every width. Putting the attribute on the full-width wrapper
     around this component made the no-drag hole cover nearly the entire
     titlebar instead of just the pill, and the macOS window stopped being
     draggable by its titlebar. This is the only place MessageSearch is
     mounted, so moving the attribute here doesn't affect another consumer. -->
<div bind:this={containerEl} data-no-drag class="relative mx-auto w-full min-w-0 sm:w-[40vw] sm:min-w-[18rem] sm:max-w-[40rem]">
  <label data-testid="search-pill" class="flex h-7 w-full items-center gap-2 rounded-md border border-edge bg-[var(--surface-alt,rgba(127,127,127,0.08))] px-2">
    <svg class="shrink-0 text-content-muted" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
    {#if barFilters.length > 0}
    <span class="flex min-w-0 max-w-[60%] shrink items-center gap-1 overflow-x-auto" data-testid="search-bar-chips">
    {#each barFilters as f (f.id)}
      <!-- Every active filter is a chip here (in:, from:, mentions:, has:, All workspaces). Its ✕
           (and Backspace on an empty field, last chip first) drops just that filter. -->
      <span
        data-testid={f.key.kind === "in" ? "search-scope-chip" : "search-bar-chip"}
        data-filter={f.id}
        class="flex h-5 shrink-0 items-center gap-1 rounded-[5px] bg-accent/20 py-0 pl-1.5 pr-1 text-caption font-semibold text-accent"
      >
        {#if f.privateChannel}in:<ChannelGlyph kind="private" class="w-3 h-3" />{f.label.slice("in:".length)}{:else}{f.label}{/if}
        <button
          type="button"
          onclick={() => removeFilter(f.key)}
          aria-label={`Remove ${f.label}`}
          class="flex items-center rounded-sm opacity-70 hover:opacity-100 cursor-pointer"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </span>
    {/each}
    </span>
    {/if}
    <input
      bind:this={inputEl}
      type="text"
      bind:value={query}
      oninput={onInput}
      onclick={() => { open = true; activeIndex = 0; }}
      onkeydown={onKeyDown}
      autocomplete="off"
      maxlength={60}
      placeholder={filteredPrompt ?? "Search messages, channels, people…"}
      aria-label={filteredPrompt ?? "Search messages, channels, and people"}
      class="h-full min-w-24 flex-1 bg-transparent text-sm text-content outline-none placeholder:text-content-muted"
    />
    {#if query || barFilters.length > 0}
      <button
        type="button"
        onclick={clear}
        aria-label="Clear search"
        class="shrink-0 rounded p-0.5 text-content-muted hover:bg-edge hover:text-content cursor-pointer"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    {/if}
  </label>

  {#if open && (trimmed.length >= MIN_QUERY_LENGTH || messagesOnly || sectionFilter || unscopedEmptyCard)}
    <div
      class="absolute left-0 right-0 top-8 z-[var(--z-overlay-backdrop)] flex max-h-[400px] flex-col overflow-hidden rounded-lg border border-edge bg-surface-alt shadow-2xl"
    >
      {#if !typeaheadActive && (!unscopedEmptyCard || messagesOnly)}{@render filterChips()}{/if}
      <div bind:this={listEl} class="min-h-0 flex-1 overflow-y-auto pb-1">
        {#if typeaheadActive}
          {@render typeaheadList()}
        {:else if scopedEmptyState}
          {@render scopedEmpty()}
        {:else if unscopedEmptyCard}
          {@render unscopedCard()}
        {:else}
          <!-- CHANNELS: joined channels plus public channels you are not in (Preview / Join). -->
          {#if channelHits.length > 0}
            {@render sectionLabel("Channels", channelHits.length)}
            <div class="px-1.5">
              {#each channelHits as hit, i (hit.channel.id)}
                {@const subline = channelSubline(hit)}
                {@const joining = joiningId === hit.channel.id}
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <div
                  data-index={i}
                  data-result-kind={hit.isMember ? "channel" : "channel-join"}
                  onmousemove={() => (activeIndex = i)}
                  class={cn("flex items-center gap-2 rounded-md px-2.5 py-1.5", activeIndex === i && "bg-raised")}
                >
                  <button type="button" onclick={() => openChannel(hit)} class="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left">
                    <span class="flex w-5 shrink-0 items-center justify-center text-content-muted">
                      {#if hit.kind === "groupdm"}
                        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      {:else if hit.channel.isPrivate}
                        <ChannelGlyph kind="private" class="w-3.5 h-3.5" />
                      {:else}
                        <ChannelGlyph kind="hash" class="w-3.5 h-3.5" />
                      {/if}
                    </span>
                    <span class="min-w-0">
                      <span class="block truncate text-sm font-medium text-content">{@render highlighted(hit.channel.name)}</span>
                      {#if subline}
                        <span class="block truncate text-caption text-content-dim">{subline}</span>
                      {/if}
                    </span>
                  </button>
                  {#if !hit.isMember}
                    <Button variant="outline" size="sm" onclick={() => previewChannel(hit.channel)}>Preview</Button>
                    <Button size="sm" disabled={joining} onclick={() => void join(hit)}>{joining ? "Joining…" : "Join"}</Button>
                  {:else if hit.kind === "groupdm"}
                    <span class="shrink-0 text-xs uppercase tracking-wide text-content-dim">Group</span>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}

          <!-- PEOPLE: a member opens their DM, an agent opens its conversation. -->
          {#if personHits.length > 0}
            {@render sectionLabel("People", personHits.length)}
            <div class="px-1.5">
              {#each personHits as hit, i (`${hit.kind}:${hit.id}`)}
                {@const idx = channelHits.length + i}
                <button
                  type="button"
                  data-index={idx}
                  data-result-kind={hit.kind}
                  onclick={() => openPerson(hit)}
                  onmousemove={() => (activeIndex = idx)}
                  class={cn("flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left", activeIndex === idx && "bg-raised")}
                >
                  <ActivityActorAvatar actorId={hit.id} actorName={hit.name} width={20} fontSize={10} />
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-sm font-medium text-content">{@render highlighted(hit.name)}</span>
                    {#if hit.email}
                      <span class="block truncate text-caption text-content-dim">{hit.email}</span>
                    {/if}
                  </span>
                  <span class="shrink-0 text-xs uppercase tracking-wide text-content-dim">{hit.kind === "dm" ? "DM" : "Agent"}</span>
                </button>
              {/each}
            </div>
          {/if}

          {#if search.loading}
            <div class="px-4 py-3 text-sm text-content-dim">Searching…</div>
          {:else if searchRefused && !searchingAll}
            <div class="px-4 py-3 text-sm text-content-dim">You don't have access to this workspace.</div>
          {:else if isEmpty && !acrossFailed}
            <div class="px-4 py-3 text-sm text-content-dim">
              No results for "{trimmed}"{searchingAll ? " in any workspace" : sectionFilter ? ` in ${SECTION_FILTER_LABEL[sectionFilter]}` : ""}
            </div>
            {#if sectionFilter === "skills"}
              {#if skillsMarketFailed}<p role="status" class="px-4 pb-2 text-xs text-content-dim">The marketplaces didn't answer.</p>{/if}
              <button type="button" onclick={seeAllSkills} class="w-full cursor-pointer px-4 pb-3 text-left text-xs font-medium text-content-dim hover:text-content">Look in Discover →</button>
            {/if}
          {:else}
            <!-- Sections follow their freshest hit (orderSections), not a fixed order. -->
            {#each search.sectionOrder as section (section)}
              {#if section === "messages"}
                {@render messagesSection()}
              {:else if section === "tasks"}
                {@render tasksSection()}
              {:else if section === "skills"}
                {@render skillsSection()}
              {:else}
                {@render pagesSection()}
              {/if}
            {/each}
          {/if}
        {/if}
        </div>
        {#if options.length > 0}
          <div class="flex items-center gap-3 border-t border-edge px-4 py-2 text-xs text-content-muted">
            <span class="flex items-center gap-1"><kbd class="rounded border border-edge px-1 py-0.5">↑</kbd><kbd class="rounded border border-edge px-1 py-0.5">↓</kbd> to navigate</span>
            <span class="flex items-center gap-1"><kbd class="rounded border border-edge px-1 py-0.5">↵</kbd> to open</span>
            {#if barFilters.length > 0}<span class="flex items-center gap-1"><kbd class="rounded border border-edge px-1 py-0.5">⌫</kbd> remove filter</span>{/if}
            <span class="flex items-center gap-1"><kbd class="rounded border border-edge px-1 py-0.5">esc</kbd> to close</span>
          </div>
        {:else if filterLabel}
          <div class="flex items-center gap-3 border-t border-edge px-4 py-2 text-xs text-content-muted">
            <span class="flex items-center gap-1"><kbd class="rounded border border-edge px-1 py-0.5">⌫</kbd> remove filter</span>
            <span class="flex items-center gap-1"><kbd class="rounded border border-edge px-1 py-0.5">esc</kbd> to close</span>
          </div>
        {/if}
    </div>
  {/if}
</div>
